import styled from '@emotion/styled';

import { EmptyReport } from '@innovamat/glimmer-assets';
import {
  EmptyState,
  InnerHtml,
  Typography,
} from '@innovamat/glimmer-components';

import { useTranslation } from 'react-i18next';
import { useCurrentNavigationValues } from '../../../../../../navigation/hooks/use-current-navigation-values';
import { TEST_TYPE } from '../../../../../modules/test/test';
import { DownloadTestReportCell } from '../../test-and-results-table/components/test-report-cell/download-test-report-cell';
import { InfoTag } from '../components/info-tag';
import { useTestAndResults } from '../context/test-and-results-provider';
import type { DetailData } from '../use-test-and-result-drawer';
import { StudentTable } from './components/student-table';

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const FlexContainer = styled.div`
  display: flex;
  gap: 16px;
  justify-content: space-between;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
  }
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 600px;
`;

const InfoTagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Description = styled(Typography.Body2)`
  margin-top: 8px;
  color: ${({ theme }) =>
    theme.tokens.color.alias.cm.text['text-subtle'].value};
`;

const Content = styled.div`
  position: relative;
  width: 100%;
`;

type Props = {
  detailData: DetailData;
  date: string;
  participation: string;
};

function TestsDrawerContent({
  detailData,
  date,
  participation,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const { rowSelected } = useTestAndResults();
  const { currentClassroom } = useCurrentNavigationValues();

  return (
    <ContentContainer>
      <FlexContainer>
        <InfoContainer>
          {rowSelected?.rowDescription && (
            <Description>
              <InnerHtml text={t(rowSelected?.rowDescription || '')} />
            </Description>
          )}
          <InfoTagsContainer>
            <InfoTag text={date} />
            <InfoTag
              title={t('mock-exams-drawer.info-tag.participation')}
              text={participation}
            />
            {rowSelected &&
              (rowSelected.type === TEST_TYPE.CONMAT ||
                rowSelected.type === TEST_TYPE.COSMOS) && (
                <DownloadTestReportCell
                  key={`report_${rowSelected.classroomTestRoundId}`}
                  testRoundId={rowSelected.classroomTestRoundId}
                  testRoundName={rowSelected.roundName}
                  report={rowSelected.report}
                  testType={rowSelected.type ?? undefined}
                  testId={rowSelected.testId}
                  classroomId={currentClassroom?.id ?? ''}
                />
              )}
          </InfoTagsContainer>
        </InfoContainer>
      </FlexContainer>
      <Content>
        {!detailData?.students?.length ? (
          <EmptyState
            Image={EmptyReport}
            title={t('mock-exams-drawer.emptyState.title')}
            subtitle={t('mock-exams-drawer.emptyState.subtitle')}
          />
        ) : (
          <StudentTable students={detailData?.students} />
        )}
      </Content>
    </ContentContainer>
  );
}

export { TestsDrawerContent };
